import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaCheckCircle, FaStepForward, FaCircle } from 'react-icons/fa';
import { useAuthContext } from '../../auth/context/AuthContext';

/**
 * OnboardingChecklist Component
 * 
 * Displays an onboarding checklist for new users with 3 steps:
 * 1. Connect bank accounts
 * 2. Add income sources  
 * 3. Share with partner (optional/skippable)
 * 
 * @param {Object} props Component props
 * @param {Object} props.dashboardData Dashboard data containing user's accounts, income, etc.
 * @param {Function} props.onOpenPartnerModal Function to open the partner connection modal
 * @returns {JSX.Element} OnboardingChecklist component
 */
const OnboardingChecklist = ({ dashboardData = {}, onOpenPartnerModal }) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSkippedPartner, setIsSkippedPartner] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Load dismissal, completion, and skip states from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('onboarding-dismissed') === 'true';
    const completed = localStorage.getItem('onboarding-completed') === 'true';
    const skipped = localStorage.getItem('onboarding-partner-skipped') === 'true';
    setIsDismissed(dismissed);
    setIsCompleted(completed);
    setIsSkippedPartner(skipped);
  }, []);

  // Check completion status for each step
  const getStepStatus = () => {
    // Step 1: Has connected accounts (check if user has any wallets/accounts)
    // PRIMARY CHECK: Use actual Account record count from database (most reliable)
    // FALLBACK: Check accounts array length and totalBalance as secondary validation
    const hasAccountRecords = (dashboardData.accountCount || 0) > 0;
    const hasAccountsArray = dashboardData.accounts?.length > 0;
    const hasBalance = dashboardData.totalBalance !== undefined && dashboardData.totalBalance?.amount !== 0;
    const hasAccounts = hasAccountRecords || hasAccountsArray || hasBalance;
    
    // Step 2: Has income sources (check if user has any income data)
    // PRIMARY CHECK: Use actual Income record count from database (most reliable)
    // FALLBACK: Check monthlyIncome.amount as secondary validation
    // NOTE: Do NOT use incomeTrends as it may contain sample data for display purposes
    const incomeCountValue = dashboardData.incomeCount ?? 0;
    const monthlyIncomeAmount = dashboardData.monthlyIncome?.amount ?? 0;
    
    const hasIncomeRecords = incomeCountValue > 0;
    const hasMonthlyIncome = monthlyIncomeAmount > 0;
    const hasIncome = hasIncomeRecords || hasMonthlyIncome;
    
    // Debug logging for income detection
    console.log('🔍 OnboardingChecklist - Income Detection:', {
      incomeCount: incomeCountValue,
      incomeCountType: typeof dashboardData.incomeCount,
      monthlyIncome: dashboardData.monthlyIncome,
      monthlyIncomeAmount: monthlyIncomeAmount,
      hasIncomeRecords,
      hasMonthlyIncome,
      hasIncome,
      fullDashboardData: dashboardData,
      note: 'Using incomeCount (actual DB records) instead of incomeTrends (may contain sample data)'
    });
    
    // Step 3: Has partner connection (check user context for partner relationship)
    const hasPartnerConnection = !!(user && (user.partnerId || user.partner || user.createdPartner));
    
    // Debug logging for partner detection
    console.log('🔍 OnboardingChecklist - Partner Detection:', {
      user: user,
      hasPartnerId: !!user?.partnerId,
      hasPartner: !!user?.partner,
      hasCreatedPartner: !!user?.createdPartner,
      hasPartnerConnection: hasPartnerConnection,
      isSkippedPartner: isSkippedPartner
    });
    
    return {
      step1: hasAccounts,
      step2: hasIncome,
      step3: hasPartnerConnection || isSkippedPartner,
      step3Skipped: isSkippedPartner && !hasPartnerConnection
    };
  };

  const stepStatus = getStepStatus();
  const completedSteps = Object.values(stepStatus).filter(Boolean).length - (stepStatus.step3Skipped ? 0 : 0);
  const totalSteps = 3;
  const isFullyComplete = stepStatus.step1 && stepStatus.step2 && stepStatus.step3;

  // Save completion status to localStorage when all steps are complete
  useEffect(() => {
    if (isFullyComplete && !isCompleted) {
      console.log('✅ OnboardingChecklist: All steps completed, saving to localStorage');
      localStorage.setItem('onboarding-completed', 'true');
      setIsCompleted(true);
    }
  }, [isFullyComplete, isCompleted]);

  // Hide if dismissed OR completed (from localStorage or current state)
  // This ensures it never shows up again after dismissal or completion, even after refresh
  if (isDismissed || isCompleted || isFullyComplete) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('onboarding-dismissed', 'true');
  };

  const handleSkipPartner = () => {
    setIsSkippedPartner(true);
    localStorage.setItem('onboarding-partner-skipped', 'true');
  };

  const handleConnectAccounts = () => {
    navigate('/wallet');
  };

  const handleAddIncome = () => {
    navigate('/cashflow?tab=income');
  };

  const handleShareAccounts = () => {
    if (onOpenPartnerModal) {
      onOpenPartnerModal();
    } else {
      // Fallback to settings if modal function not provided
      navigate('/settings');
    }
  };

  const StepIcon = ({ completed, skipped }) => {
    if (completed && !skipped) {
      return <FaCheckCircle className="w-5 h-5 text-green-500" />;
    } else if (skipped) {
      return <FaStepForward className="w-5 h-5 text-gray-400" />;
    } else {
      return <FaCircle className="w-5 h-5 text-gray-300" />;
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-800">
            Get Started with MnyMny
          </h3>
          <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
            {completedSteps}/{totalSteps} Complete
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FaTimes className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {/* Step 1: Connect Accounts */}
        <div className="flex items-center gap-4">
          <StepIcon completed={stepStatus.step1} />
          <div className="flex-1">
            <h4 className="font-medium text-gray-800">Connect Bank Accounts</h4>
            <p className="text-sm text-gray-600">
              Link your bank accounts to automatically track transactions and balances
            </p>
          </div>
          {!stepStatus.step1 && (
            <button
              onClick={handleConnectAccounts}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 transition-colors"
            >
              Connect Now
            </button>
          )}
        </div>

        {/* Step 2: Add Income */}
        <div className="flex items-center gap-4">
          <StepIcon completed={stepStatus.step2} />
          <div className="flex-1">
            <h4 className="font-medium text-gray-800">Add Income Sources</h4>
            <p className="text-sm text-gray-600">
              Set up your income sources for accurate budget and spending analysis
            </p>
          </div>
          {!stepStatus.step2 && (
            <button
              onClick={handleAddIncome}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 transition-colors"
            >
              Add Income
            </button>
          )}
        </div>

        {/* Step 3: Share with Partner (Optional) */}
        <div className="flex items-center gap-4">
          <StepIcon completed={stepStatus.step3} skipped={stepStatus.step3Skipped} />
          <div className="flex-1">
            <h4 className="font-medium text-gray-800">
              Share with Partner
              <span className="text-sm text-gray-500 ml-2">(Optional)</span>
            </h4>
            <p className="text-sm text-gray-600">
              Share selected accounts with your partner for joint financial management
            </p>
          </div>
          {!stepStatus.step3 && (
            <div className="flex gap-2">
              <button
                onClick={handleShareAccounts}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 transition-colors"
              >
                Share Accounts
              </button>
              <button
                onClick={handleSkipPartner}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-300 transition-colors"
              >
                Skip
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Completion Message */}
      {completedSteps === totalSteps && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 text-sm font-medium">
            🎉 Great job! You've completed the basic setup. You can now enjoy all of MnyMny's features.
          </p>
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist; 